import React, { useEffect, useMemo, useState } from 'react';
import { BulkActionBar, Button, Card, ClampText, EmptyValue, Form, Modal, RowActions, StatusBadge, Table } from '@/components/ui';
import type { StatusVariant } from '../../../utils/statusUtils';
import { Vehicle, VehicleDefect } from '../../../types/Vehicle';
import type { Employee } from '../../../types/Employee';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import { employeeService } from '../../../services/employeeService';
import { useAuth } from '../../../contexts/AuthContext';
import { notifyCriticalDefectsChanged } from '../../../components/awaitingApprovalsCount';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { formatDate, todayPacific } from '../../../utils/wrapDate';
import {
  DEFECT_CATEGORIES,
  DEFECT_OUTCOME_LABEL,
  defectParts,
  defectSourceLabel,
  defectStatusLabel,
  personName,
  pluralize
} from '../utils/equipmentDisplay';
import { printDefectReport } from '../utils/equipmentPrint';
import EquipmentDateRangeModal from './EquipmentDateRangeModal';

interface EquipmentDefectsTabProps {
  vehicle: Vehicle;
  /** Manage Repairs: add, resolve, sign off and mark duplicate (EQ-04). */
  canManage: boolean;
  onUpdated: () => Promise<void>;
}

type StatusKey = 'Open' | 'AwaitingSignOff' | 'Closed';
const STATUS_FILTERS: Array<{ key: StatusKey; label: string }> = [
  { key: 'Open', label: 'Open' },
  { key: 'AwaitingSignOff', label: 'Awaiting Sign-Off' },
  { key: 'Closed', label: 'Closed' }
];
/** EQ-07: the tab opens on what still needs someone. */
const DEFAULT_STATUSES: StatusKey[] = ['Open', 'AwaitingSignOff'];

const STATUS_VARIANT: Record<string, StatusVariant> = { Open: 'warning', AwaitingSignOff: 'teal', Closed: 'secondary' };
const SEVERITY_VARIANT: Record<string, StatusVariant> = { Critical: 'danger', High: 'warning', Medium: 'info', Low: 'secondary' };
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">{message}</p> : null;

const Required = () => <span className="tw-text-danger" aria-hidden="true"> *</span>;

interface ResolveState {
  defects: VehicleDefect[];
  outcome: 'Repaired' | 'Duplicate' | 'NoFaultFound';
  remedy: string;
  fixedById: string;
  fixedAt: string;
  signOff: boolean;
  duplicateOfId: string;
}

/** The closing line of the Resolve dialog, matching the count and the outcome (V3-073). */
export function resolveOutcomeText(count: number, signOff: boolean): string {
  const subject = count === 1 ? 'The defect' : `The ${count} defects`;
  if (signOff) return `${subject} ${count === 1 ? 'closes' : 'close'} now, signed off by you.`;
  return `${subject} ${count === 1 ? 'moves' : 'move'} to Awaiting Sign-Off.`;
}

/** The result shown above the table after Resolve (STATE-06, LANG-09). */
function resolveResult(count: number, signOff: boolean): string {
  const what = pluralize(count, 'defect');
  return signOff ? `${what} resolved and closed.` : `${what} resolved – awaiting sign-off.`;
}

/**
 * Defects tab (EQ-07, EQ-12, EQ-13): one Resolve Defect step for one defect or several, with
 * Fixed By, Date Fixed and an optional sign-off; a status filter; and Add a Defect with the shared
 * category list. Results show in the table where the user acted (STATE-06).
 */
const EquipmentDefectsTab: React.FC<EquipmentDefectsTabProps> = ({ vehicle, canManage, onUpdated }) => {
  const { currentUser } = useAuth();
  const defects = useMemo(() => vehicle.defects ?? [], [vehicle.defects]);
  const [statuses, setStatuses] = useState<StatusKey[]>(DEFAULT_STATUSES);
  const [selected, setSelected] = useState<number[]>([]);
  const [showExport, setShowExport] = useState(false);
  const [categories, setCategories] = useState<string[]>([...DEFECT_CATEGORIES]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [add, setAdd] = useState<{ category: string; description: string; comments: string; severity: string } | null>(null);
  const [resolve, setResolve] = useState<ResolveState | null>(null);
  const [signOff, setSignOff] = useState<VehicleDefect[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);

  useEffect(() => {
    if (!canManage) return;
    vehicleService.getDefectCategories().then(setCategories).catch(() => undefined);
    employeeService
      .getActiveEmployeesForSelection()
      .then((rows) => setEmployees(rows))
      .catch(() => undefined);
  }, [canManage]);

  const shown = useMemo(
    () =>
      defects
        .filter((d) => statuses.includes(d.status as StatusKey))
        .sort((a, b) => {
          const order = (s: string) => (s === 'Open' ? 0 : s === 'AwaitingSignOff' ? 1 : 2);
          return order(a.status) - order(b.status) || new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime();
        }),
    [defects, statuses]
  );
  // Open defects can be resolved together, defects awaiting sign-off signed off together (V3-053).
  const selectableShown = shown.filter((d) => d.status !== 'Closed');
  const selectedOpen = defects.filter((d) => selected.includes(d.id) && d.status === 'Open');
  const selectedAwaiting = defects.filter((d) => selected.includes(d.id) && d.status === 'AwaitingSignOff');

  useEffect(() => {
    // Selection only holds defects that still need someone and are still on screen.
    setSelected((ids) => ids.filter((id) => selectableShown.some((d) => d.id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  const toggleStatus = (key: StatusKey) =>
    setStatuses((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]));

  const finish = async (message: string) => {
    setNotice({ tone: 'success', text: message });
    setSelected([]);
    notifyCriticalDefectsChanged();
    await onUpdated();
  };

  const openResolve = (rows: VehicleDefect[]) => {
    setErrors({});
    setResolve({
      defects: rows,
      outcome: 'Repaired',
      remedy: '',
      fixedById: currentUser?.id ? String(currentUser.id) : '',
      fixedAt: todayPacific(),
      signOff: false,
      duplicateOfId: ''
    });
  };

  const submitResolve = async () => {
    if (!resolve) return;
    const next: Record<string, string> = {};
    if (resolve.outcome === 'Repaired' && !resolve.remedy.trim()) next.remedy = 'Remedy required';
    if (resolve.outcome === 'Duplicate' && !resolve.duplicateOfId) next.duplicateOfId = 'Choose the defect these duplicate';
    if (!resolve.fixedById) next.fixedById = 'Choose who fixed it';
    if (!resolve.fixedAt) next.fixedAt = 'Date Fixed required';
    else if (resolve.fixedAt > todayPacific()) next.fixedAt = "Date Fixed can't be in the future";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    try {
      const done = await vehicleService.resolveDefects(vehicle.id, {
        defectIds: resolve.defects.map((d) => d.id),
        outcome: resolve.outcome,
        remedy: resolve.remedy.trim() || undefined,
        fixedById: Number(resolve.fixedById),
        fixedAt: resolve.fixedAt,
        signOff: resolve.signOff,
        duplicateOfId: resolve.duplicateOfId ? Number(resolve.duplicateOfId) : undefined
      });
      setResolve(null);
      await finish(resolveResult(done.length, resolve.signOff));
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  const submitSignOff = async () => {
    if (!signOff) return;
    setSaving(true);
    setErrors({});
    try {
      const done = await vehicleService.signOffDefects(vehicle.id, signOff.map((d) => d.id));
      setSignOff(null);
      await finish(`${pluralize(done.length, 'defect')} signed off and closed.`);
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  const submitAdd = async () => {
    if (!add) return;
    const next: Record<string, string> = {};
    if (!add.category) next.category = 'Category required';
    if (!add.description.trim()) next.description = 'Defect required';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    try {
      const comments = add.comments.trim();
      const created = await vehicleService.raiseDefect(vehicle.id, {
        category: add.category,
        description: comments ? `${add.description.trim()}\n${comments}` : add.description.trim(),
        severity: add.severity,
        source: 'Wrap'
      });
      setAdd(null);
      setStatuses((list) => (list.includes('Open') ? list : [...list, 'Open']));
      notifyCriticalDefectsChanged();
      setNotice(created.warning ? { tone: 'warning', text: created.warning } : { tone: 'success', text: 'Defect added.' });
      await onUpdated();
    } catch (err) {
      setErrors(fieldErrorsOf(err));
    } finally {
      setSaving(false);
    }
  };

  const duplicateChoices = resolve
    ? defects.filter((d) => !resolve.defects.some((r) => r.id === d.id))
    : [];
  const defectLabel = (d: VehicleDefect) => {
    const parts = defectParts(d);
    return `#${d.id} ${parts.category ? `${parts.category}: ` : ''}${parts.defect} (${formatDate(d.reportedAt) ?? ''})`;
  };
  const allSelected = selectableShown.length > 0 && selectableShown.every((d) => selected.includes(d.id));

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <Card>
        <Card.Header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <h2 className="tw-mb-0 tw-text-base tw-font-semibold">Defects</h2>
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setShowExport(true)}>
              <i className="bi bi-download tw-mr-1" aria-hidden="true" />
              Export Defects
            </Button>
            {canManage && (
              <Button
                size="sm"
                onClick={() => {
                  setErrors({});
                  setAdd({ category: '', description: '', comments: '', severity: 'Medium' });
                }}
              >
                <i className="bi bi-plus-lg tw-mr-1" aria-hidden="true" />
                Add a Defect
              </Button>
            )}
          </div>
        </Card.Header>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-border-b tw-border-line tw-px-4 tw-py-3">
          <span className="tw-text-sm tw-font-semibold tw-text-content-secondary" id="defect-status-filter">Status</span>
          <div role="group" aria-labelledby="defect-status-filter" className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
            {STATUS_FILTERS.map((option) => (
              <Form.Check
                key={option.key}
                id={`defect-status-${option.key}`}
                type="checkbox"
                label={option.label}
                checked={statuses.includes(option.key)}
                onChange={() => toggleStatus(option.key)}
              />
            ))}
          </div>
          {canManage && (
            <div className="tw-ml-auto">
              <BulkActionBar
                count={selectedOpen.length + selectedAwaiting.length}
                noun="defect"
                emptyText="Select defects to resolve or sign them off together"
                onClearSelection={() => setSelected([])}
                actions={[
                  { key: 'resolve', label: 'Resolve Selected', hidden: selectedOpen.length === 0, onClick: () => openResolve(selectedOpen) },
                  {
                    key: 'sign-off',
                    label: 'Sign Off Selected',
                    hidden: selectedAwaiting.length === 0,
                    onClick: () => {
                      setErrors({});
                      setSignOff(selectedAwaiting);
                    }
                  }
                ]}
              />
            </div>
          )}
        </div>
        <Card.Body className="tw-p-0">
          {notice && (
            <p
              role="status"
              className={`tw-mb-0 tw-border-b tw-border-line tw-px-4 tw-py-2 tw-text-sm ${notice.tone === 'warning' ? 'tw-text-warning-text' : 'tw-text-success'}`}
            >
              {notice.text}
            </p>
          )}
          {shown.length === 0 ? (
            <p className="tw-mb-0 tw-p-4 tw-text-sm tw-text-content-muted">
              {defects.length === 0 ? 'No defects on this unit.' : 'No defects match the selected statuses.'}
            </p>
          ) : (
            <Table responsive className="tw-mb-0">
              <thead>
                <tr>
                  {canManage && (
                    <th className="tw-w-10">
                      <Form.Check
                        type="checkbox"
                        aria-label="Select all defects that are not closed"
                        checked={allSelected}
                        disabled={selectableShown.length === 0}
                        onChange={() => setSelected(allSelected ? [] : selectableShown.map((d) => d.id))}
                      />
                    </th>
                  )}
                  <th>Status</th>
                  <th>Severity</th>
                  <th>Category</th>
                  <th className="tw-min-w-[14rem]">Defect</th>
                  <th>Source</th>
                  <th>Reported</th>
                  <th>Reported By</th>
                  <th className="tw-min-w-[12rem]">Remedy</th>
                  <th>Fixed By</th>
                  <th>Date Fixed</th>
                  <th>Signed Off By</th>
                  <th>Sign-Off Date</th>
                  <th className="tw-text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {shown.map((defect) => {
                  const parts = defectParts(defect);
                  const isOpen = defect.status === 'Open';
                  return (
                    <tr key={defect.id}>
                      {canManage && (
                        <td>
                          {defect.status !== 'Closed' && (
                            <Form.Check
                              type="checkbox"
                              aria-label={`Select defect ${defect.id}`}
                              checked={selected.includes(defect.id)}
                              onChange={() =>
                                setSelected((ids) => (ids.includes(defect.id) ? ids.filter((x) => x !== defect.id) : [...ids, defect.id]))
                              }
                            />
                          )}
                        </td>
                      )}
                      <td>
                        <StatusBadge status={defect.status} label={defectStatusLabel(defect.status)} variant={STATUS_VARIANT[defect.status] ?? 'secondary'} />
                      </td>
                      <td>
                        <StatusBadge status={defect.severity} label={defect.severity} variant={SEVERITY_VARIANT[defect.severity] ?? 'secondary'} />
                      </td>
                      <td>{parts.category || <EmptyValue />}</td>
                      <td>
                        <ClampText>{parts.defect || defect.description}</ClampText>
                        {parts.comments && <p className="tw-mb-0 tw-mt-1 tw-whitespace-pre-wrap tw-text-xs tw-text-content-muted">{parts.comments}</p>}
                        {defect.outcome === 'Duplicate' && defect.duplicateOfId && (
                          <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">Duplicate of defect #{defect.duplicateOfId}</p>
                        )}
                      </td>
                      <td className="tw-whitespace-nowrap">{defectSourceLabel(defect.source)}</td>
                      <td className="tw-whitespace-nowrap">{formatDate(defect.reportedAt) ?? <EmptyValue />}</td>
                      <td>{defect.reporterName || personName(defect.reportedBy) || <EmptyValue />}</td>
                      <td className="tw-whitespace-pre-wrap">
                        {defect.outcome && defect.outcome !== 'Repaired' ? `${DEFECT_OUTCOME_LABEL[defect.outcome]}${defect.remedy ? `: ${defect.remedy}` : ''}` : defect.remedy || <EmptyValue />}
                      </td>
                      <td>{personName(defect.fixedBy ?? defect.remediedBy) || <EmptyValue />}</td>
                      <td className="tw-whitespace-nowrap">{formatDate(defect.fixedAt ?? defect.remediedAt) ?? <EmptyValue />}</td>
                      <td>{personName(defect.signedOffBy) || <EmptyValue />}</td>
                      <td className="tw-whitespace-nowrap">{formatDate(defect.signedOffAt) ?? <EmptyValue />}</td>
                      <td className="tw-text-right">
                        {canManage && defect.status !== 'Closed' && (
                          <RowActions
                            actions={[
                              { kind: 'custom', label: 'Resolve Defect', icon: 'bi-wrench-adjustable', hidden: !isOpen, overflow: true, onClick: () => openResolve([defect]) },
                              {
                                kind: 'custom',
                                label: 'Sign Off',
                                icon: 'bi-check2-circle',
                                hidden: defect.status !== 'AwaitingSignOff',
                                overflow: true,
                                onClick: () => {
                                  setErrors({});
                                  setSignOff([defect]);
                                }
                              }
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={add !== null} onHide={() => setAdd(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Add a Defect</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-secondary">Unit {vehicle.unitNumber}</p>
          <Form.Label htmlFor="defect-category">Category<Required /></Form.Label>
          <Form.Select id="defect-category" value={add?.category ?? ''} onChange={(e) => setAdd((a) => a && { ...a, category: e.target.value })}>
            <option value="">Select a Category</option>
            {categories.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.category} />
          <Form.Label htmlFor="defect-description" className="tw-mt-3">Defect<Required /></Form.Label>
          <Form.Control
            id="defect-description"
            value={add?.description ?? ''}
            placeholder="What Is Wrong?"
            onChange={(e) => setAdd((a) => a && { ...a, description: e.target.value })}
          />
          <FieldError message={errors.description} />
          <Form.Label htmlFor="defect-comments" className="tw-mt-3">Comments</Form.Label>
          <Form.Control id="defect-comments" as="textarea" rows={3} value={add?.comments ?? ''} onChange={(e) => setAdd((a) => a && { ...a, comments: e.target.value })} />
          <Form.Label htmlFor="defect-severity" className="tw-mt-3">Severity<Required /></Form.Label>
          <Form.Select id="defect-severity" value={add?.severity ?? 'Medium'} onChange={(e) => setAdd((a) => a && { ...a, severity: e.target.value })}>
            {SEVERITIES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </Form.Select>
          <FieldError message={errors.severity || errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setAdd(null)} disabled={saving}>Cancel</Button>
          <Button onClick={submitAdd} disabled={saving}>{saving ? 'Saving…' : 'Add Defect'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={resolve !== null} onHide={() => setResolve(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{resolve && resolve.defects.length > 1 ? `Resolve ${resolve.defects.length} Defects` : 'Resolve Defect'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {resolve && (
            <>
              <ul className="tw-mb-3 tw-list-none tw-space-y-1 tw-rounded-md tw-border tw-border-line tw-bg-surface-alt tw-p-3 tw-text-sm">
                {resolve.defects.map((d) => {
                  const parts = defectParts(d);
                  return (
                    <li key={d.id}>
                      <span className="tw-font-semibold">{parts.category ? `${parts.category}: ` : ''}</span>
                      {parts.defect}
                      <span className="tw-text-xs tw-text-content-muted"> – {defectSourceLabel(d.source)}, {d.reporterName}, {formatDate(d.reportedAt)}</span>
                    </li>
                  );
                })}
              </ul>
              <fieldset>
                <legend className="tw-mb-1 tw-text-sm tw-font-semibold">Outcome<Required /></legend>
                <div className="tw-flex tw-flex-wrap tw-gap-x-4">
                  {(['Repaired', 'Duplicate', 'NoFaultFound'] as const).map((value) => (
                    <Form.Check
                      key={value}
                      id={`outcome-${value}`}
                      type="radio"
                      name="defect-outcome"
                      label={DEFECT_OUTCOME_LABEL[value]}
                      checked={resolve.outcome === value}
                      onChange={() => setResolve((r) => r && { ...r, outcome: value })}
                    />
                  ))}
                </div>
                <FieldError message={errors.outcome} />
              </fieldset>
              {resolve.outcome === 'Duplicate' && (
                <div className="tw-mt-3">
                  <Form.Label htmlFor="defect-duplicate-of">Duplicate Of<Required /></Form.Label>
                  <Form.Select
                    id="defect-duplicate-of"
                    value={resolve.duplicateOfId}
                    onChange={(e) => setResolve((r) => r && { ...r, duplicateOfId: e.target.value })}
                  >
                    <option value="">Select the Original Defect</option>
                    {duplicateChoices.map((d) => (
                      <option key={d.id} value={d.id}>{defectLabel(d)}</option>
                    ))}
                  </Form.Select>
                  <FieldError message={errors.duplicateOfId} />
                </div>
              )}
              <div className="tw-mt-3">
                <Form.Label htmlFor="defect-remedy">
                  Remedy{resolve.outcome === 'Repaired' && <Required />}
                </Form.Label>
                <Form.Control
                  id="defect-remedy"
                  as="textarea"
                  rows={3}
                  value={resolve.remedy}
                  placeholder={resolve.outcome === 'Repaired' ? 'What Was Done to Fix It?' : 'Optional'}
                  onChange={(e) => setResolve((r) => r && { ...r, remedy: e.target.value })}
                />
                <FieldError message={errors.remedy} />
              </div>
              <div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
                <div>
                  <Form.Label htmlFor="defect-fixed-by">Fixed By<Required /></Form.Label>
                  <SearchableDropdown<Employee>
                    options={employees}
                    value={resolve.fixedById}
                    onChange={(value) => setResolve((r) => r && { ...r, fixedById: value })}
                    getValue={(e) => e.id}
                    formatOption={(e) => personName(e)}
                    placeholder={currentUser ? personName(currentUser) : 'Select an Employee'}
                  />
                  <FieldError message={errors.fixedById} />
                </div>
                <div>
                  <Form.Label htmlFor="defect-fixed-at">Date Fixed<Required /></Form.Label>
                  <Form.Control
                    id="defect-fixed-at"
                    type="date"
                    max={todayPacific()}
                    value={resolve.fixedAt}
                    onChange={(e) => setResolve((r) => r && { ...r, fixedAt: e.target.value })}
                  />
                  <FieldError message={errors.fixedAt} />
                </div>
              </div>
              <Form.Check
                className="tw-mt-3"
                id="defect-sign-off"
                type="checkbox"
                label="Sign Off"
                checked={resolve.signOff}
                onChange={(e) => setResolve((r) => r && { ...r, signOff: e.target.checked })}
              />
              <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">{resolveOutcomeText(resolve.defects.length, resolve.signOff)}</p>
              <FieldError message={errors.defectIds || errors._form} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setResolve(null)} disabled={saving}>Cancel</Button>
          <Button onClick={submitResolve} disabled={saving}>
            {saving ? 'Saving…' : resolve?.signOff ? 'Resolve and Sign Off' : 'Resolve'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={signOff !== null} onHide={() => setSignOff(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{signOff && signOff.length > 1 ? `Sign Off ${signOff.length} Defects` : 'Sign Off Defect'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {signOff?.map((d) => {
            const parts = defectParts(d);
            return (
              <dl key={d.id} className="tw-mb-3 tw-grid tw-grid-cols-3 tw-gap-x-3 tw-gap-y-2 tw-rounded-md tw-border tw-border-line tw-bg-surface-alt tw-p-3 tw-text-sm">
                <dt className="tw-font-semibold">Defect</dt>
                <dd className="tw-col-span-2 tw-mb-0">{parts.category ? `${parts.category}: ` : ''}{parts.defect}</dd>
                <dt className="tw-font-semibold">Remedy</dt>
                <dd className="tw-col-span-2 tw-mb-0">{d.remedy || <EmptyValue />}</dd>
                <dt className="tw-font-semibold">Fixed By</dt>
                <dd className="tw-col-span-2 tw-mb-0">{personName(d.fixedBy ?? d.remediedBy) || <EmptyValue />}</dd>
                <dt className="tw-font-semibold">Date Fixed</dt>
                <dd className="tw-col-span-2 tw-mb-0">{formatDate(d.fixedAt ?? d.remediedAt) ?? <EmptyValue />}</dd>
              </dl>
            );
          })}
          <p className="tw-mb-0 tw-text-sm">
            {signOff && signOff.length > 1 ? `The ${signOff.length} defects close, signed off by you.` : 'The defect closes, signed off by you.'}
          </p>
          <FieldError message={errors.defectIds || errors._form} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setSignOff(null)} disabled={saving}>Cancel</Button>
          <Button onClick={submitSignOff} disabled={saving}>{saving ? 'Saving…' : 'Sign Off'}</Button>
        </Modal.Footer>
      </Modal>

      <EquipmentDateRangeModal
        show={showExport}
        title="Export Defects"
        description="Print every defect reported on this unit in the date range."
        confirmLabel="Export"
        onHide={() => setShowExport(false)}
        onConfirm={({ from, to }) =>
          printDefectReport({
            vehicle,
            from,
            to,
            load: async () => (await vehicleService.exportDefects(vehicle.id, { from: from.toISOString(), to: to.toISOString() })).defects
          })
        }
      />
    </div>
  );
};

export default EquipmentDefectsTab;
